import { HttpClient } from '@angular/common/http';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, map, Observable, tap, throwError } from 'rxjs';
import { environment } from '@env/environment';
import * as FingerprintJS from '@fingerprintjs/fingerprintjs';
import { LocalStorageService } from './localstorage.service';
import { toast } from 'ngx-sonner';
import { DeviceService } from './device.service';

@Injectable({
  providedIn: 'root'
})
export class SecurityService {

  hasInternetSubject = new BehaviorSubject<boolean>(true);
  consultor = new BehaviorSubject<any>(null);
  completeDeviceInfo: any = null;
  private onboardingData: WritableSignal<any> = signal({
  });

  constructor(private httpClient: HttpClient,
              private router: Router,
              private localstorageService: LocalStorageService,
              private deviceService: DeviceService,
    ) {
      const phone = localStorage.getItem('phone');
      const consultorName = localStorage.getItem('consultorName');
      const qr = localStorage.getItem('qr');
      const user = localStorage.getItem('user');
      const agency = localStorage.getItem('agency');
      const data = {
        phone: phone,
        name: consultorName,
        user: user,
        agency: agency,
        qrImage: qr,
      };
      this.consultor.next(data);
     }

  getNewSession(consultor: any) {
    return this.httpClient.post(`${environment.serverUrl}/checkSession`, { session: null, consultor: consultor });
  }

  getResumeSession(identification: number) {
    return this.httpClient.post(`${environment.serverUrl}/resumeSession`, { identification: identification });
  }

  setOnboardinData(onboardingData: any){
    this.onboardingData.set(onboardingData);
  }

  setValidateData(data: { code: string; message: string; type: string; data: { procedureId: number; expirationDate: string; creationDate: string; state: string; userId: number; identificationNumber: string; documentType: string; levelIdentity: number; transactionValidation: { validationId: number; validatorName: string; result: string; transactionValidationStatusId: number; transactionValidationStatus: string; }[]; documents: never[]; failedRedirectUrl: string; successRedirectUrl: string; data: string; userName: string; attribute: string; procedureType: number; originatorNames: string; originatorLastnames: string; }; traceId: string; }) {
    return this.httpClient.post(`${ environment.serverUrl }/setValidateData`, { data });
  }

  getOnboardingData(){
    return this.onboardingData();
  }

  setConsultor(internalCode: string,) {
    localStorage.removeItem('consultor');
    localStorage.removeItem('qr');
    localStorage.removeItem('phone');
    localStorage.removeItem('consultorName');
    localStorage.removeItem('user');
    localStorage.removeItem('agency');
    const session = localStorage.getItem('session');

    return this.httpClient.post(`${ environment.serverUrl }/setConsultor`, {consultor:internalCode, session})
    .pipe(
      tap((response:any) => {
        this.localstorageService.set('consultor', internalCode);
        this.localstorageService.set('session', response.token);
        this.localstorageService.set('qr', `${environment.serverUrl}/assets/qr/${ response.qrCode }`);
        this.localstorageService.set('phone', response.phone);
        this.localstorageService.set('consultorName', response.name);
        this.localstorageService.set('user', response.user);
        this.consultor.next({
            phone: response.phone,
            name: response.name,
            user: response.user,
            qrImage:`${environment.serverUrl}/assets/qr/${ response.qrCode }`
        });
      }),
      tap(() => {
        this.collectDevicePayload().then((payload) => {
          this.completeDeviceInfo = payload;
          this.setDeviceData(payload).subscribe({
            next: () => {},
            error: (error: any) => {
              console.error('Error', error);
            }
          });
        });
      }),
      catchError((error: any) => {
        toast.error('No fue posible encontrar el asesor que intentas consultar.');
        return throwError(() => error);
      })
    )

  }

  getTypedPersonData() {
    return this.httpClient.post(`${ environment.serverUrl }/getTypedPersonData`, {})
    .pipe(
      catchError((error: any) => {
        if(error.status === 0) {
          this.hasInternetSubject.next(false);
        }
        if (error.status === 401 || (error.status === 403)) {
          this.localstorageService.clear();
          this.router.navigate(['/']);
          return this.getNewSession(null);
        }
        return error;
      }),
      map((response: any) => {
        this.hasInternetSubject.next(true);
        return response;
      })
    )
    ;

  }

  getConsultorInfo(){
    return this.consultor.getValue();
  }

  getDeviceInfo(){
    return this.deviceService.getDeviceInfo();
  }

  async collectDevicePayload(): Promise<Record<string, unknown>> {
    const deviceInfo = this.getDeviceInfo();
    let fingerprint = '';
    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      fingerprint = result.visitorId;
    } catch (error) {
      console.error('Error obteniendo fingerprint:', error);
    }

    return {
      ...deviceInfo,
      fingerprint,
      web_version: localStorage.getItem('version') ?? '',
    };
  }

  getBalance() {
    return this.httpClient.post(`${ environment.serverUrl }/getBalance`, {});
  }

  setBalance(data: { incomeExtraActivity: number; outcome: number; assets: number; liabilities: number; }) {
    return this.httpClient.post(`${ environment.serverUrl }/setBalance`, data);
  }

  getForeign() {
    return this.httpClient.post(`${ environment.serverUrl }/getForeign`, {});
  }

  setForeign(value: any) {
    return this.httpClient.post(`${ environment.serverUrl }/setForeign`, value);
  }

  setForeningHasPublicJobManager(value:any) {
    return this.httpClient.post(`${ environment.serverUrl }/setForeningHasPublicJobManager`, value);
  }

  sendOTP() {
    return this.httpClient.post(`${ environment.serverUrl }/sendOTP`, { });
  }

  validateOTP(code: string) {
    return this.httpClient.post(`${ environment.serverUrl }/checkOTP`, { code });
  }

  getPrincipalActivityTypeId(){
    return this.httpClient.get(`${ environment.serverUrl }/getPrincipalActivityTypeId`);
  }

  getCategories(){
    return this.httpClient.get(`${environment.serverUrl}/app/listSavingCategory/getCategoryList`)
  }

  validateOnboardingTodayByIdentification(identification:string){
    return this.httpClient.post(`${ environment.serverUrl }/validateOnboardingTodayByIdentification`, {identification})
  }

  calculateQuota(quota: string | number) {
    return this.httpClient.post(`${environment.serverUrl}/app/contributions/calculateQuota`, {quota});
  }

  getConsultor() {
    return this.httpClient.post(`${ environment.serverUrl }/getConsultor`, {})
    .pipe(
      catchError((error: any) => {
        if(error.status === 0) {
          this.hasInternetSubject.next(false);
        }
        if (error.status === 401 || (error.status === 403)) {
          localStorage.clear();
          this.router.navigate(['/']);
          return this.getNewSession(null);
        }
        return error;
      }),
      tap((response: any) => {
        if(!!response.name) {
          localStorage.setItem('qr', `${environment.serverUrl}/assets/qr/${ response.qrCode }`);
          localStorage.setItem('phone', response.phone);
          localStorage.setItem('consultorName', response.name);
          localStorage.setItem('user', response.user);
          localStorage.setItem('agency', response.agency);

          this.consultor.next({
            phone: response.phone,
            name: response.name,
            user: response.user,
            agency: response.agency,
            qrImage: `${environment.serverUrl}/assets/qr/${ response.qrCode }`
          });

        }
      }),
      map((response: any) => {
        this.hasInternetSubject.next(true);
        return response;
      }),
    )
    ;
  }

  getCheckpoint(){
    return this.httpClient.get(`${environment.serverUrl}/getCheckpoint`)
  }

  setActiveServices(activeServices: string[]) {
    return this.httpClient.post(`${ environment.serverUrl }/setActiveServices`, {activeServices});
  }

  setDeviceData(deviceData: any) {
    return this.httpClient.post(`${ environment.serverUrl }/app/deviceCustomer/addDevice`, deviceData);
  }

  getServices() {
    return this.httpClient.post(`${ environment.serverUrl }/getActiveServices`, {})
    .pipe(
      catchError((error: any) => {
        if(error.status === 0) {
          this.hasInternetSubject.next(false);
        }
        if (error.status === 401 || (error.status === 403)) {
          localStorage.clear();
          this.router.navigate(['/']);
          return this.getNewSession(null);
        }
        return error;
      }),
      map((response: any) => {
        this.hasInternetSubject.next(true);
        return response;
      })
    )
    ;
  }

  //Validation Identification
  checkBlockIdentification(identification: string | number) {
    return this.httpClient.post(`${ environment.serverUrl }/checkIdentification`, {
      nIdentificacion: String(identification).trim(),
    });
  }

  setTypedPersonData(
    identificationType: string | number,
    identification: string,
    email: string,
    phone: string,
    fullname: string,
    nationalityId: string | number,
    expeditionCityId?: number | null,
    birthday?: string | null
  ) {
    return this.httpClient.post(`${ environment.serverUrl }/setTypedPersonData`,
      { identificationType, identification, email, phone, fullname, nationalityId, expeditionCityId, birthday }
    );
  }

  setBirthdate(birthdate: string | Date) {
    return this.httpClient.post(`${ environment.serverUrl }/setBirthdate`, { birthdate });
  }

  checkAddress(address: any) {
    return this.httpClient.post(`${ environment.serverUrl }/checkAddress`, {address});
  }

  confirmAddress(valid: boolean) {
    return this.httpClient.post(`${ environment.serverUrl }/validateAddress`, { valid });
  }

  setAddressData(addressData: any) {
    return this.httpClient.post(`${ environment.serverUrl }/setAddressData`, {addressData});
  }

  getAddressData() {
    return this.httpClient.post(`${ environment.serverUrl }/getAddressData`, {});
  }

  setPapData(papData: any) {
    return this.httpClient.post(`${ environment.serverUrl }/app/pap/addPap`, {papData});
  }

  getPDF() {
    return this.httpClient.post(`${ environment.serverUrl }/getPDF`, {});
  }

  getInterfazApp() {
    return this.httpClient.get(`${environment.serverUrl}/getInterfazApp`)
  }

  getVinculationWhatsappQr() {
    return this.httpClient.get(`${environment.serverUrl}/vinculationWhatsappQr`);
  }

  getQuota() {
    return this.httpClient.get(`${environment.serverUrl}/app/contributions/getQuota`);
  }

  showPapData() {
    return this.httpClient.get(`${ environment.serverUrl }/app/pap/showPap`);
  }

  getConsultorPercents(consultorUser: string): Observable<any> {
    return this.httpClient.post(`${environment.serverUrl}/app/listConsultor/getConsultorPercents`, {consultorUser})
  }

  getFrontVersion(): Observable<any> {
    return this.httpClient.get(`${environment.serverUrl}/getFrontVersion`,)
  }

}
